import { generateApiActionTypes } from 'utils/actions/generateActionTypes'
import { decamelizeKeysWithNumbers } from 'utils/api/decamelizeKeysWithNumbers'
import { FP_ENDPOINTS } from 'apps/fleet-planner/config'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { ILoad, IFileCreate, TFileType, IAction } from 'apps/fleet-planner/types'
import { addFileTypeToDocumentList } from '../api'
import { normalizeStops } from '../api/loadDetails'
import { mapRemovedFiles } from '../fileUtils'

export interface ISuccessAction extends IAction {
  payload: {
    shipment: ILoad
  }
}

export const FETCH_LOAD_DETAILS_ACTION_TYPES = generateApiActionTypes('FETCH_LOAD_DETAILS')
export const UPDATE_LOAD_STATUS_ACTION_TYPES = generateApiActionTypes('UPDATE_LOAD_STATUS')
export const UPDATE_LOAD_ACTION_TYPES = generateApiActionTypes('UPDATE_LOAD')
export const DELETE_LOAD_ACTION_TYPES = generateApiActionTypes('DELETE_LOAD')
export const UPLOAD_ATTACHMENT_ACTION_TYPES = generateApiActionTypes('UPLOAD_ATTACHMENT')
export const DELETE_ATTACHMENT_ACTION_TYPES = generateApiActionTypes('DELETE_ATTACHMENT')
export const DELETE_STOP_ACTION_TYPES = generateApiActionTypes('DELETE_STOP')
export const UPDATE_LOAD_TRUCK_ACTION_TYPES = generateApiActionTypes('UPDATE_LOAD_TRUCK')
export const UPDATE_LOAD_TRAILER_ACTION_TYPES = generateApiActionTypes('UPDATE_LOAD_TRAILER')
export const UPDATE_LOAD_DISPATCHER_ACTION_TYPES = generateApiActionTypes('UPDATE_LOAD_DISPATCHER')
export const UPDATE_LOAD_DRIVERS_ACTION_TYPES = generateApiActionTypes('UPDATE_LOAD_DRIVERS')
export const UPDATE_LOAD_ASSIGNMENT_ACTION_TYPES = generateApiActionTypes('UPDATE_LOAD_ASSIGNMENT')

export const updateLoadTruckSuccess = (shipment: ILoad | null | undefined) => ({
  type: UPDATE_LOAD_TRUCK_ACTION_TYPES.UPDATE_LOAD_TRUCK_SUCCESS,
  payload: {
    shipment,
  },
})

export const updateLoadTrailerSuccess = (shipment: ILoad | null | undefined) => ({
  type: UPDATE_LOAD_TRAILER_ACTION_TYPES.UPDATE_LOAD_TRAILER_SUCCESS,
  payload: {
    shipment,
  },
})

export const updateLoadDispatcherSuccess = (shipment: ILoad | null | undefined) => ({
  type: UPDATE_LOAD_DISPATCHER_ACTION_TYPES.UPDATE_LOAD_DISPATCHER_SUCCESS,
  payload: {
    shipment,
  },
})

export const updateLoadDriversSuccess = (shipment: ILoad) => ({
  type: UPDATE_LOAD_DRIVERS_ACTION_TYPES.UPDATE_LOAD_DRIVERS_SUCCESS,
  payload: {
    shipment,
  },
})

export const updateLoadAssignmentSuccess = (shipment: ILoad) => ({
  type: UPDATE_LOAD_ASSIGNMENT_ACTION_TYPES.UPDATE_LOAD_ASSIGNMENT_SUCCESS,
  payload: {
    shipment,
  },
})

export const fetchLoadDetails = (id: string) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: FP_ENDPOINTS.loads,
      method: 'GET',
      id,
    },
    actionTypes: Object.keys(FETCH_LOAD_DETAILS_ACTION_TYPES),
  },
  type: '',
})

export const updateLoadStatus = (
  load: ILoad,
  status: LOAD_STATE,
  currentStopSequence?: string,
  eta?: string
) => {
  const { id } = load || {}
  const body = {
    currentStopSequence,
    status,
    eta,
  }
  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: FP_ENDPOINTS.loads,
        method: 'PUT',
        id,
        group: 'status',
        body,
      },
      actionTypes: Object.keys(UPDATE_LOAD_STATUS_ACTION_TYPES),
    },
    type: '',
  }
}

export const addAttachments = (load: ILoad, newFiles: IFileCreate[], isPod: boolean) => {
  const { pods = [], attachments = [], id, stops = [] } = load || {}
  const oldAttachments = isPod ? pods : attachments
  const newAttachments = [...oldAttachments, ...newFiles]
  const newLoad = {
    ...load,
    stops: normalizeStops(stops),
    [isPod ? 'pods' : 'attachments']: newAttachments,
  }

  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: FP_ENDPOINTS.loads,
        method: 'PUT',
        id,
        body: { shipment: newLoad },
      },
      actionTypes: Object.keys(UPLOAD_ATTACHMENT_ACTION_TYPES),
    },
  }
}
export const deleteAttachment = (id: number, load: ILoad, isPod: boolean) => {
  const { pods = [], attachments = [], id: loadId, stops = [] } = load || {}
  const oldAttachments = isPod ? pods : attachments
  const newAttachments = mapRemovedFiles(oldAttachments, id)

  const newLoad = {
    ...load,
    stops: normalizeStops(stops),
    [isPod ? 'pods' : 'attachments']: newAttachments,
  }

  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: FP_ENDPOINTS.loads,
        method: 'PUT',
        id: loadId,
        body: { shipment: newLoad },
      },
      actionTypes: Object.keys(DELETE_ATTACHMENT_ACTION_TYPES),
    },
  }
}

export const replaceAttachment = (
  replacedId: number,
  load: ILoad,
  newFiles: IFileCreate[],
  fileType: TFileType
) => {
  const { pods = [], attachments = [], id, stops = [] } = load || {}
  const privateAttachments = addFileTypeToDocumentList(attachments, 'private')
  const podAttachments = addFileTypeToDocumentList(pods, 'pod')
  const newAttachments = addFileTypeToDocumentList(newFiles, fileType)
  const allAttachments = [...privateAttachments, ...podAttachments, ...newAttachments]
  const attachmentsWithoutRemoved = mapRemovedFiles(allAttachments, replacedId)

  const newLoad = {
    ...load,
    stops: normalizeStops(stops),
    allAttachments: [...attachmentsWithoutRemoved],
  }

  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: FP_ENDPOINTS.loads,
        method: 'PUT',
        id,
        body: { shipment: newLoad },
      },
      actionTypes: Object.keys(DELETE_ATTACHMENT_ACTION_TYPES),
    },
  }
}

export const updateLoad = (load: ILoad) => {
  const { id: loadId, truck, trailer, stops = [] } = load || {}
  const { id: truckId } = truck || {}
  const { id: trailerId } = trailer || {}
  const equipmentNumbers = {
    ...(trailerId && { trailerId }),
    ...(truckId && { truckId }),
  }
  const shipment = decamelizeKeysWithNumbers({
    ...load,
    ...equipmentNumbers,
    stops: normalizeStops(stops),
  })
  return {
    payload: {
      callApi: true,
      requestData: {
        endpoint: FP_ENDPOINTS.loads,
        method: 'PUT',
        id: loadId,
        body: { shipment },
      },
      actionTypes: Object.keys(UPDATE_LOAD_ACTION_TYPES),
    },
  }
}

export const deleteLoad = (loadId: string, isFleet?: boolean) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: FP_ENDPOINTS.loads,
      method: 'DELETE',
      id: loadId,
    },
    actionTypes: Object.keys(DELETE_LOAD_ACTION_TYPES),
    isFleet,
  },
})

export const deleteStop = (stopId: string, loadId: string) => ({
  payload: {
    callApi: true,
    requestData: {
      endpoint: FP_ENDPOINTS.loads,
      id: loadId,
      method: 'DELETE',
      group: `stops/${stopId}`,
    },
    actionTypes: Object.keys(DELETE_STOP_ACTION_TYPES),
  },
  type: '',
})

export const RESET_LOAD_DETAILS = 'RESET_LOAD_DETAILS'
export const resetLoadDetails = () => ({
  type: RESET_LOAD_DETAILS,
})

export const uploadAttachmentSuccess = (shipment: ILoad): ISuccessAction => ({
  type: UPLOAD_ATTACHMENT_ACTION_TYPES.UPLOAD_ATTACHMENT_SUCCESS,
  payload: {
    shipment,
  },
})

export const uploadAttachmentFailure = (shipment: ILoad): ISuccessAction => ({
  type: UPLOAD_ATTACHMENT_ACTION_TYPES.UPLOAD_ATTACHMENT_FAILURE,
  payload: {
    shipment,
  },
})

export const deleteAttachmentSuccess = (shipment: ILoad): ISuccessAction => ({
  type: DELETE_ATTACHMENT_ACTION_TYPES.DELETE_ATTACHMENT_SUCCESS,
  payload: {
    shipment,
  },
})
